/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { groupBy } from 'lodash';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, TemporaryDrawer } from '@coursera/cds-core';
import { ArrowPreviousIcon, ChevronNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import Navigation from 'bundles/unified-seo-content-common/components/hub-page/Navigation';
import { PAGE_TYPE, SEO_CONTENT_PAGE_SLUG } from 'bundles/unified-seo-content-common/constants';
import { usePageInfo } from 'bundles/unified-seo-content-common/contexts/PageInfoContext';
import type { NavigationLinkWithSubItems } from 'bundles/unified-seo-content-common/types';
import { scrollPageToTop } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

const { HeadingGroup, Content } = TemporaryDrawer;

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type Props = {
  activeCategorySlug: string;
  navigationLinks: NavigationLinkWithSubItems[];
};

const styles = {
  mobileCategoryNavigation: css`
    border: 1px solid var(--cds-color-grey-600);
    border-radius: 4px;
  `,
  backHomeLink: css`
    padding: 8px;
  `,
  navigationButton: css`
    max-width: 100%;
    color: var(--cds-color-neutral-primary);
    width: 100%;
    font-weight: 400;
    justify-content: space-between;
  `,
  categoryList: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  categoryListItem: css`
    padding: 16px;

    a,
    a:hover {
      display: block;
      width: 100%;
      text-decoration: none;

      span {
        color: var(--cds-color-neutral-primary);
      }
    }
  `,
  activeCategoryListItem: css`
    background-color: var(--cds-color-neutral-background-primary-weak);

    span {
      font-weight: bold;
    }
  `,
};

const MobileCategoryNavigation = ({ activeCategorySlug, navigationLinks }: Props) => {
  const [isOpen, setOpen] = React.useState(false);
  const { pageType } = usePageInfo();

  const toggleDrawer = () => {
    setOpen((prevState) => !prevState);
  };

  const onNavigationLinkClick = () => {
    toggleDrawer();
    scrollPageToTop();
  };

  const groupedNavigationLinks = groupBy(navigationLinks, 'pageType');
  const categoryPagesLinks = groupedNavigationLinks?.[PAGE_TYPE.categoryHub];
  const hubPageLink = groupedNavigationLinks?.[PAGE_TYPE.hubWithCategories]?.[0];

  const isHubPage = activeCategorySlug === SEO_CONTENT_PAGE_SLUG[pageType];

  const activeCategoryInfo = categoryPagesLinks?.find((link) => link.slug === activeCategorySlug);
  const activeCategoryLabel = isHubPage || !activeCategoryInfo ? _t('Categories') : activeCategoryInfo?.label;

  return (
    <div className="rc-MobileCategoryNavigation" css={styles.mobileCategoryNavigation}>
      <TrackedButton
        css={styles.navigationButton}
        size="medium"
        variant="ghost"
        onClick={toggleDrawer}
        icon={<ChevronNextIcon />}
        iconPosition="after"
        trackingName="mobile_navigation_button_trigger"
      >
        {activeCategoryLabel}
      </TrackedButton>
      <TemporaryDrawer open={isOpen} anchor="left" sticky="none" width="medium" onClose={toggleDrawer}>
        <HeadingGroup
          backAction={
            !isHubPage && hubPageLink ? (
              <Button
                css={(theme) => ({ marginLeft: `calc(var(--cds-spacing-100) * -1)` })}
                component={TrackedLink2}
                href={hubPageLink.href}
                icon={<ArrowPreviousIcon size="small" />}
                iconPosition="before"
                size="small"
                variant="ghost"
                trackingName="mobile_navigation_home_link"
              >
                {hubPageLink.label}
              </Button>
            ) : undefined
          }
        >
          {_t('Categories')}
        </HeadingGroup>

        <Content>
          <Navigation
            activeCategorySlug={activeCategorySlug}
            navigationLinks={categoryPagesLinks}
            onNavigationLinkClick={onNavigationLinkClick}
          />
        </Content>
      </TemporaryDrawer>
    </div>
  );
};

export default MobileCategoryNavigation;
